import {
  addDays,
  endOfMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
} from "date-fns";

import type {
  AmpBoardTaskGroupsQueryVariables,
  AmpTaskRowFragment,
  AmpTasksBoolExp,
  AmpTasksOrderBy,
} from "src/graphql";
import {
  type CompletedVisibility,
  DUE_BUCKETS,
  type DueBucket,
  type GroupKind,
  NO_ASSIGNEE,
  NOBODY,
  type Ordering,
  resolveMe,
  type TaskFilters,
  type TaskGroup,
  UNTAGGED,
} from "src/pages/projects/task-view";
import { ARCHIVED, endOfComingWeek } from "src/pages/projects/utils";

/** How many tasks a view loads at a time. */
export const PAGE_SIZE = 100;

const MATCH_NOTHING: AmpTasksBoolExp = { id: { _in: [] } };

/** Which tasks a view holds, and whether one made in this browser belongs there. */
export type TaskScope = {
  where: AmpTasksBoolExp;
  includes: (task: AmpTaskRowFragment) => boolean;
};

/** Top-level tasks in open projects; subtasks load with their parent. */
export const OPEN_PROJECTS_TASKS: AmpTasksBoolExp = {
  parent_task_id: { _is_null: true },
  project: { deleted_at: { _is_null: true } },
};

export function projectTasksWhere(projectId: string): AmpTasksBoolExp {
  return { project_id: { _eq: projectId }, parent_task_id: { _is_null: true } };
}

/** Neither done nor archived. */
export const OPEN_TASK: AmpTasksBoolExp = {
  deleted_at: { _is_null: true },
  _not: { status: { category: { _eq: "done" } } },
};

/** Soonest due first; undated after, most recently touched first, as the inbox's cards read. */
export const URGENT_FIRST: AmpTasksOrderBy[] = [
  { due_at: "asc_nulls_last" },
  { updated_at: "desc" },
  { id: "asc" },
];

/** A task user row naming one of the users, directly or through a group they belong to. */
export function taskUserIsAnyOf(userIds: string[]) {
  return {
    _or: [
      { user_id: { _in: userIds } },
      { user_group: { members: { user_id: { _in: userIds } } } },
    ],
  };
}

export function assignedTo(userId: string): AmpTasksBoolExp {
  return { users: { type: { _eq: "assignee" }, ...taskUserIsAnyOf([userId]) } };
}

function anyOf(clauses: AmpTasksBoolExp[]): AmpTasksBoolExp {
  if (clauses.length === 0) return MATCH_NOTHING;
  return clauses.length === 1
    ? (clauses[0] ?? MATCH_NOTHING)
    : { _or: clauses };
}

/** Where today and the coming week end, as `dueBucket` reads them. */
function dueBounds(now: Date) {
  return {
    now: now.toISOString(),
    tomorrow: addDays(startOfDay(now), 1).toISOString(),
    weekEnd: endOfComingWeek(now).toISOString(),
  };
}

function dueWhere(bucket: DueBucket, now: Date): AmpTasksBoolExp {
  const bounds = dueBounds(now);
  switch (bucket) {
    case "overdue":
      return { due_at: { _lt: bounds.now } };
    case "today":
      return { due_at: { _gte: bounds.now, _lt: bounds.tomorrow } };
    case "week":
      return { due_at: { _gte: bounds.tomorrow, _lt: bounds.weekEnd } };
    case "later":
      return { due_at: { _gte: bounds.weekEnd } };
    case "none":
      return { due_at: { _is_null: true } };
  }
}

/** A group on a task counts as someone, as `matchesPeople` reads it. */
function peopleWhere(
  type: "assignee" | "owner",
  ids: string[],
  noneId: string,
): AmpTasksBoolExp {
  const userIds = ids.filter((id) => id !== noneId);
  return anyOf([
    ...(userIds.length > 0
      ? [{ users: { type: { _eq: type }, ...taskUserIsAnyOf(userIds) } }]
      : []),
    ...(ids.includes(noneId)
      ? [{ _not: { users: { type: { _eq: type } } } }]
      : []),
  ]);
}

function escapeLike(value: string): string {
  return value.replace(/[\\%_]/g, (character) => `\\${character}`);
}

/** The server's side of `filterTasks`, so a view loads only the tasks it shows. */
export function viewWhere(
  scope: AmpTasksBoolExp,
  {
    filters,
    search,
    completed,
    currentUserId,
    now,
  }: {
    filters: TaskFilters;
    search: string;
    completed: CompletedVisibility;
    currentUserId: string | undefined;
    now: Date;
  },
): AmpTasksBoolExp {
  const states = filters.statusCategories;
  const clauses: AmpTasksBoolExp[] = [scope];
  if (!states.some((state) => state === "done" || state === ARCHIVED)) {
    if (completed === "none") clauses.push(OPEN_TASK);
    if (completed === "week") {
      clauses.push({
        _or: [
          OPEN_TASK,
          { updated_at: { _gte: subDays(now, 7).toISOString() } },
        ],
      });
    }
  }
  if (states.length > 0) {
    const categories = states.filter((state) => state !== ARCHIVED);
    clauses.push(
      anyOf([
        ...(categories.length > 0
          ? [
              {
                deleted_at: { _is_null: true },
                status: { category: { _in: categories } },
              },
            ]
          : []),
        ...(states.includes(ARCHIVED)
          ? [{ deleted_at: { _is_null: false } }]
          : []),
      ]),
    );
  }
  if (filters.assigneeIds.length > 0) {
    clauses.push(
      peopleWhere(
        "assignee",
        resolveMe(filters.assigneeIds, currentUserId),
        NO_ASSIGNEE,
      ),
    );
  }
  if (filters.creatorIds.length > 0) {
    clauses.push({
      created_by: { _in: resolveMe(filters.creatorIds, currentUserId) },
    });
  }
  if (filters.projectIds.length > 0) {
    clauses.push({ project_id: { _in: filters.projectIds } });
  }
  const query = search.trim();
  if (query) clauses.push({ title: { _ilike: `%${escapeLike(query)}%` } });
  return { _and: clauses };
}

// `sortTasks` breaks ties by title, then id, in both directions.
const TIEBREAK: AmpTasksOrderBy[] = [{ title: "asc" }, { id: "asc" }];

/** The server's side of `sortTasks`, so pages arrive in the order the view shows. */
export function taskOrderBy(
  ordering: Ordering,
  descending: boolean,
): AmpTasksOrderBy[] {
  const direction = descending ? "desc" : "asc";
  switch (ordering) {
    case "manual":
      return [{ position: "asc" }, ...TIEBREAK];
    case "status":
      return [
        { deleted_at: descending ? "desc_nulls_last" : "asc_nulls_first" },
        { status: { category_rank: direction } },
        { status: { position: direction } },
        ...TIEBREAK,
      ];
    case "dueDate":
      return [
        { due_at: descending ? "desc_nulls_last" : "asc_nulls_last" },
        ...TIEBREAK,
      ];
    case "created":
      return [{ created_at: direction }, ...TIEBREAK];
    case "updated":
      return [{ updated_at: direction }, ...TIEBREAK];
    case "title":
      return [{ title: direction }, { id: "asc" }];
  }
}

/** The tasks a group of this kind holds, as `groupTasks` places them. */
export function groupWhere(
  kind: GroupKind,
  group: TaskGroup<unknown>,
  now: Date,
): AmpTasksBoolExp {
  switch (kind) {
    case "none":
      return {};
    case "status":
      if (group.isArchive) return { deleted_at: { _is_null: false } };
      return {
        deleted_at: { _is_null: true },
        ...(group.icon.kind === "status"
          ? { status_id: { _eq: group.icon.status.id } }
          : { status: { category: { _eq: group.category } } }),
      };
    case "assignee":
    case "owner": {
      const type = { _eq: kind };
      if (group.key === NOBODY) return { _not: { users: { type } } };
      return {
        users: group.personId
          ? { type, user_id: { _eq: group.personId } }
          : { type, user_group_id: { _eq: group.userGroupId } },
      };
    }
    case "project":
      return { project_id: { _eq: group.projectId } };
    case "dueDate": {
      const bucket = DUE_BUCKETS.find((candidate) => candidate === group.key);
      return bucket ? dueWhere(bucket, now) : MATCH_NOTHING;
    }
    case "tag":
      return group.key === UNTAGGED
        ? { _not: { tags: {} } }
        : { tags: { tag_id: { _eq: group.key } } };
    case "creator":
      return group.key === NOBODY
        ? { created_by: { _is_null: true } }
        : { created_by: { _eq: group.key } };
  }
}

/** The tasks due in the whole weeks a calendar shows around a month, in local time. */
export function dueInMonthView(month: Date): AmpTasksBoolExp {
  return {
    due_at: {
      _gte: startOfWeek(startOfMonth(month)).toISOString(),
      _lt: addDays(startOfWeek(endOfMonth(month)), 7).toISOString(),
    },
  };
}

/** Asks the groups query for this grouping's counts only. */
export function taskGroupsVariables(
  where: AmpTasksBoolExp,
  kind: GroupKind,
  now: Date,
): AmpBoardTaskGroupsQueryVariables {
  return {
    where,
    ...dueBounds(now),
    personType: kind === "owner" ? "owner" : "assignee",
    byStatus: kind === "status",
    byPerson: kind === "assignee" || kind === "owner",
    byProject: kind === "project",
    byTag: kind === "tag",
    byCreator: kind === "creator",
    byDueDate: kind === "dueDate",
  };
}
